// AiChatInputBar — input-baren längst ner i chat-vyn: intent-väljare
// + templates-meny + autoHint, diagram-pipeline-preview, layout-
// validator-checkbox (admin), settings-knapp, quick-action-knapp,
// prompt-input och skicka-knapp.
// Extraherad ur AiAssistantDialog (#1 i PRE_RELEASE_REVIEW_100).

import { Settings, Send, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { IntentSelector, type IntentChoice } from '@/components/workflow/IntentSelector';
import { IntentTemplatesMenu } from '@/components/workflow/IntentTemplatesMenu';
import { AiDiagramQuickAction, type DiagramTemplate } from '@/components/ai/AiDiagramQuickAction';
import { AiDiagramPipelinePreview } from '@/components/ai/AiDiagramPipelinePreview';
import { AiTokenBudgetChip } from '@/components/ai/AiTokenBudgetChip';
import { suggestSkills } from '@/lib/aiSkills/skillRegistry';
import type { IntentClassification } from '@/lib/intentTypes';
import type { UseDiagramPipelineResult } from '@/hooks/useDiagramPipeline';
import type { WorkflowState, MultiSheetWorkflowState } from '@/types/workflow';

interface AiChatInputBarProps {
  input: string;
  setInput: (v: string) => void;
  inputRef: React.RefObject<HTMLInputElement>;
  isLoading: boolean;
  banned: boolean;
  isAdmin: boolean;
  intentChoice: IntentChoice;
  setIntentChoice: (c: IntentChoice) => void;
  lastAutoClassification: IntentClassification | null;
  validateLayoutEnabled: boolean;
  setValidateLayoutEnabled: (v: boolean) => void;
  diagramPipeline: UseDiagramPipelineResult;
  onImport: (state: WorkflowState | MultiSheetWorkflowState, overLimit?: boolean) => void;
  onViewIn3D?: () => void;
  onOpenChange: (open: boolean) => void;
  onOpenSettings: () => void;
  onSend: () => void;
  /** #25: stoppar pågående AI-stream när isLoading=true. */
  onStop: () => void;
  /** #25: true mellan abort() och att stream-loop:ens finally återställer isLoading. */
  isStopping: boolean;
  /** #27: aktuell model + honing-promptens storlek så token-chippen kan estimera. */
  modelName: string;
  honingPromptChars: number;
  /** #27: snapshot-getter — vi räknar dess characters in i token-budget:en. */
  getSnapshot?: () => WorkflowState | MultiSheetWorkflowState | undefined;
}

export function AiChatInputBar({
  input,
  setInput,
  inputRef,
  isLoading,
  banned,
  isAdmin,
  intentChoice,
  setIntentChoice,
  lastAutoClassification,
  validateLayoutEnabled,
  setValidateLayoutEnabled,
  diagramPipeline,
  onImport,
  onViewIn3D,
  onOpenChange,
  onOpenSettings,
  onSend,
  onStop,
  isStopping,
  modelName,
  honingPromptChars,
  getSnapshot,
}: AiChatInputBarProps) {
  return (
    <div className="border-t p-3 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <IntentSelector
          value={intentChoice}
          onChange={setIntentChoice}
          disabled={isLoading || banned}
          autoHint={
            intentChoice === 'auto' && lastAutoClassification
              ? {
                  intent: lastAutoClassification.intent,
                  confidence: lastAutoClassification.confidence,
                }
              : null
          }
        />
        <IntentTemplatesMenu
          disabled={isLoading || banned}
          onPickTemplate={(tpl) => {
            setInput(tpl.prompt);
            setIntentChoice(tpl.intent);
          }}
        />
        <div className="text-[10px] text-muted-foreground">
          {intentChoice === 'auto'
            ? 'Klassificeraren tolkar din prompt'
            : 'Manuell intent — klassificeraren skippas'}
        </div>
        {/* #27: live token-budget — hjälper user se när prompten närmar
            sig context-window:n innan AI:n silent-truncerar. */}
        <div className="ml-auto">
          <AiTokenBudgetChip
            input={input}
            model={modelName}
            honingPromptChars={honingPromptChars}
            getSnapshot={getSnapshot}
          />
        </div>
      </div>
      {/* D11/W3/X3: cost-preview + live pipeline-stepper.
          - Idle: visar estimate från planDiagramFromPrompt + all-pending steps.
          - Running/done: visar live state från useDiagramPipeline. */}
      {!banned && (input.trim().length > 0 && suggestSkills(input, { isAdmin }).length > 0 || diagramPipeline.status !== 'idle') && (
        <AiDiagramPipelinePreview
          input={input}
          isLoading={isLoading}
          diagramPipeline={diagramPipeline}
          onImport={onImport}
          onViewIn3D={onViewIn3D}
          onOpenChange={onOpenChange}
        />
      )}
      {isAdmin && (
        <div className="flex items-center gap-2 px-1 pb-1 text-xs text-muted-foreground">
          <Checkbox
            id="validate-layout"
            checked={validateLayoutEnabled}
            onCheckedChange={(checked) => setValidateLayoutEnabled(checked === true)}
            disabled={isLoading || banned}
          />
          <Label
            htmlFor="validate-layout"
            className="cursor-pointer text-xs font-normal"
            title="Kör layoutValidator på AI:ns workflow-JSON. Errors skickas tillbaka till AI:n som retry-feedback (max 2 försök per send)."
          >
            Validera layout (admin) — kasta tillbaka till AI vid regelbrott
          </Label>
        </div>
      )}
      <div className="flex gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="flex-shrink-0"
          onClick={onOpenSettings}
          title="AI Settings"
        >
          <Settings className="w-4 h-4" />
        </Button>
        <AiDiagramQuickAction
          onSelectTemplate={(tpl: DiagramTemplate) => {
            setInput(tpl.prompt);
            inputRef.current?.focus();
          }}
          disabled={isLoading || banned}
          isAdmin={isAdmin}
        />
        <Input
          ref={inputRef}
          placeholder={banned ? 'Access revoked' : 'Describe your workflow...'}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && onSend()}
          disabled={isLoading || banned}
        />
        {isLoading ? (
          <Button
            onClick={onStop}
            disabled={isStopping}
            variant="destructive"
            size="icon"
            aria-label={isStopping ? 'Stopping AI stream…' : 'Stop AI stream'}
            data-testid="ai-stop-button"
          >
            {isStopping ? (
              <span className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full" />
            ) : (
              <Square className="w-4 h-4" fill="currentColor" />
            )}
          </Button>
        ) : (
          <Button onClick={onSend} disabled={!input.trim() || banned} size="icon">
            <Send className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
